import type { FC, ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { usePortal, type UsePortal } from 'src/hooks/use-portal';
import { assertContext } from 'src/utils/assert-context';

type InfiniteScrollingMode = 'before-threshold' | 'after-threshold' | 'default';

type ContextValue = {
  contextualSidebarPortal: UsePortal;
  registerForInfiniteScrolling: boolean;
  setRegisterForInfiniteScrolling: (
    registerForInfiniteScrolling: boolean
  ) => void;
  infiniteScrollingMode: InfiniteScrollingMode;
  setInfiniteScrollingMode: (
    infiniteScrollingMode: InfiniteScrollingMode
  ) => void;
};

// pass undefined as any, because we run assertContext at runtime
const AppShellContext = createContext<ContextValue>(undefined as any);

export function useAppShell() {
  const context = useContext(AppShellContext);
  assertContext(context, 'AppShell');
  return context;
}

export const AppShellProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const contextualSidebarPortal = usePortal();

  const [registerForInfiniteScrolling, setRegisterForInfiniteScrolling] =
    useState(false);
  const [infiniteScrollingMode, setInfiniteScrollingMode] =
    useState<InfiniteScrollingMode>('default');

  const value = useMemo(
    () => ({
      contextualSidebarPortal,
      registerForInfiniteScrolling,
      setRegisterForInfiniteScrolling,
      infiniteScrollingMode,
      setInfiniteScrollingMode,
    }),
    [
      contextualSidebarPortal,
      registerForInfiniteScrolling,
      setRegisterForInfiniteScrolling,
      infiniteScrollingMode,
      setInfiniteScrollingMode,
    ]
  );

  return (
    <AppShellContext.Provider value={value}>
      {children}
    </AppShellContext.Provider>
  );
};
